import { useState, useEffect, useCallback } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { 
  LayoutDashboard, 
  Home, 
  Briefcase, 
  Users, 
  DollarSign, 
  MessageSquare, 
  HelpCircle, 
  Mail, 
  FileText, 
  LogOut,
  Menu,
  X,
  CreditCard,
  Settings,
  BarChart3,
  Bot,
  Shield,
  Activity,
  Zap
} from "lucide-react";
import AdminLogin from "./AdminLogin";
import HeroEditor from "./editors/HeroEditor";
import ServicesEditor from "./editors/ServicesEditor";
import TeamEditor from "./editors/TeamEditor";
import SectorsEditor from "./editors/SectorsEditor";
import PricingEditor from "./editors/PricingEditor";
import TestimonialsEditor from "./editors/TestimonialsEditor";
import FAQEditor from "./editors/FAQEditor";
import ContactEditor from "./editors/ContactEditor";
import BlogEditor from "./editors/BlogEditor";
import ContactsView from "./views/ContactsView";
import PaymentsView from "./views/PaymentsView";
import LeadsView from "./views/LeadsView";
import AnalyticsDashboard from "./views/AnalyticsDashboard";
import AdminUsersView from "./views/AdminUsersView";
import ActivityLogView from "./views/ActivityLogView";
import TrialsView from "./views/TrialsView";
import AdminInvoicesView from "./views/AdminInvoicesView";
import LeadNotifications from "@/components/LeadNotifications";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [auth, setAuth] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leadCount, setLeadCount] = useState(0);
  const [trialCount, setTrialCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check for stored auth
    const storedAuth = sessionStorage.getItem("adminAuth");
    if (storedAuth) {
      const [username, password] = atob(storedAuth).split(":");
      setAuth({ username, password });
    } else {
      setLoading(false);
    }
  }, []);

  const fetchContent = useCallback(async () => {
    try {
      const [contentRes, leadsRes, trialsRes] = await Promise.all([
        axios.get(`${API}/admin/content`, { auth: { username: auth.username, password: auth.password } }),
        axios.get(`${API}/admin/leads`, { auth: { username: auth.username, password: auth.password } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/trials`, { auth: { username: auth.username, password: auth.password } }).catch(() => ({ data: { total: 0 } }))
      ]);
      setContent(contentRes.data);
      setLeadCount(leadsRes.data?.items?.length || leadsRes.data?.total || 0);
      setTrialCount(trialsRes.data?.total || 0);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    if (auth) {
      fetchContent();
    }
  }, [auth, fetchContent]);

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    sessionStorage.removeItem("admin_auth");
    setAuth(null);
  };

  const updateSection = async (section, data) => {
    try {
      await axios.put(`${API}/admin/content/${section}`, data, {
        auth: { username: auth.username, password: auth.password }
      });
      await fetchContent();
      return true;
    } catch (error) {
      toast.error("Error al actualizar seccion");
      return false;
    }
  };

  if (!auth) {
    return <AdminLogin onLogin={setAuth} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#02040a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const menuItems = [
    { path: "/admin", icon: BarChart3, label: "Dashboard", exact: true },
    { path: "/admin/hero", icon: Home, label: "Hero" },
    { path: "/admin/services", icon: Briefcase, label: "Servicios" },
    { path: "/admin/team", icon: Users, label: "Equipo" },
    { path: "/admin/sectors", icon: Settings, label: "Sectores" },
    { path: "/admin/pricing", icon: DollarSign, label: "Precios" },
    { path: "/admin/testimonials", icon: MessageSquare, label: "Testimonios" },
    { path: "/admin/faq", icon: HelpCircle, label: "FAQs" },
    { path: "/admin/contact", icon: Mail, label: "Contacto" },
    { path: "/admin/blog", icon: FileText, label: "Blog" },
    { divider: true },
    { path: "/admin/leads", icon: Users, label: "Leads", badge: leadCount },
    { path: "/admin/contacts", icon: Mail, label: "Mensajes" },
    { path: "/admin/payments", icon: CreditCard, label: "Pagos" },
    { path: "/admin/trials", icon: Zap, label: "Pruebas Gratis", badge: trialCount },
    { path: "/admin/invoices", icon: FileText, label: "Facturacion e-CF" },
    { divider: true },
    { path: "/admin/users", icon: Shield, label: "Usuarios Admin" },
    { path: "/admin/activity", icon: Activity, label: "Log Actividad" },
    { path: "/asesor", icon: Bot, label: "Asesor IA", external: true, highlight: true },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#02040a] flex">
      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0b1221] border-r border-white/5 transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#3b82f6] flex items-center justify-center transform -skew-x-6">
                <span className="text-white font-bold text-xl skew-x-6">G</span>
              </div>
              <div>
                <span className="text-white font-semibold block">Admin Panel</span>
                <span className="text-[#94a3b8] text-xs">Global Metafora</span>
              </div>
            </Link>
          </div>

          {/* Menu */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-1">
              {menuItems.map((item, index) => (
                item.divider ? (
                  <li key={index} className="my-4 border-t border-white/5" />
                ) : (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      data-testid={`menu-${item.path.replace("/admin/", "") || "dashboard"}`}
                      className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                        item.highlight
                          ? "bg-gradient-to-r from-[#06b6d4]/10 to-[#10b981]/10 border border-[#06b6d4]/20 text-[#06b6d4] hover:from-[#06b6d4]/20 hover:to-[#10b981]/20"
                          : (item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path))
                            ? "bg-[#3b82f6]/20 text-[#3b82f6]"
                            : "text-[#94a3b8] hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="bg-[#3b82f6] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                          {item.badge}
                        </span>
                      )}
                    </Link>
                  </li>
                )
              ))}
            </ul>
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-white/5">
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="flex items-center gap-3 px-4 py-3 w-full text-[#94a3b8] hover:bg-red-500/10 hover:text-red-400 rounded transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#0b1221] text-white rounded"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-y-auto">
        {/* Admin Top Bar */}
        <div className="h-14 border-b border-white/5 bg-[#02040a]/80 backdrop-blur-sm flex items-center justify-end px-6 flex-shrink-0 sticky top-0 z-20">
          <div className="flex items-center gap-2">
            <LeadNotifications auth={auth} />
          </div>
        </div>
        <div className="flex-1 p-6 lg:p-8">
        <Routes>
          <Route path="/" element={<AnalyticsDashboard auth={auth} content={content} />} />
          <Route path="/hero" element={<HeroEditor content={content?.hero} onSave={(data) => updateSection("hero", data)} />} />
          <Route path="/services" element={<ServicesEditor content={content?.services} onSave={(data) => updateSection("services", data)} />} />
          <Route path="/team" element={<TeamEditor content={content?.team} onSave={(data) => updateSection("team", data)} />} />
          <Route path="/sectors" element={<SectorsEditor content={content?.sectors} onSave={(data) => updateSection("sectors", data)} />} />
          <Route path="/pricing" element={<PricingEditor content={content?.pricing} onSave={(data) => updateSection("pricing", data)} />} />
          <Route path="/testimonials" element={<TestimonialsEditor content={content?.testimonials} onSave={(data) => updateSection("testimonials", data)} />} />
          <Route path="/faq" element={<FAQEditor content={content?.faq} onSave={(data) => updateSection("faq", data)} />} />
          <Route path="/contact" element={<ContactEditor content={content?.contact} onSave={(data) => updateSection("contact", data)} />} />
          <Route path="/blog/*" element={<BlogEditor auth={auth} />} />
          <Route path="/contacts" element={<ContactsView auth={auth} />} />
          <Route path="/leads" element={<LeadsView auth={auth} />} />
          <Route path="/payments" element={<PaymentsView auth={auth} />} />
          <Route path="/users" element={<AdminUsersView auth={auth} />} />
          <Route path="/activity" element={<ActivityLogView auth={auth} />} />
          <Route path="/trials" element={<TrialsView auth={auth} />} />
          <Route path="/invoices" element={<AdminInvoicesView auth={auth} />} />
        </Routes>
        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

const DashboardHome = ({ content }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
      <p className="text-[#94a3b8] mb-8">Bienvenido al panel de administración de Global Metafora APP</p>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="glass-card p-6">
          <h3 className="text-[#94a3b8] text-sm mb-2">Servicios</h3>
          <p className="text-3xl font-bold text-white">{content?.services?.services?.length || 0}</p>
        </div>
        <div className="glass-card p-6">
          <h3 className="text-[#94a3b8] text-sm mb-2">Planes</h3>
          <p className="text-3xl font-bold text-white">{content?.pricing?.plans?.length || 0}</p>
        </div>
        <div className="glass-card p-6">
          <h3 className="text-[#94a3b8] text-sm mb-2">Testimonios</h3>
          <p className="text-3xl font-bold text-white">{content?.testimonials?.testimonials?.length || 0}</p>
        </div>
        <div className="glass-card p-6">
          <h3 className="text-[#94a3b8] text-sm mb-2">FAQs</h3>
          <p className="text-3xl font-bold text-white">{content?.faq?.faqs?.length || 0}</p>
        </div>
      </div>

      <div className="glass-card p-6">
        <h2 className="text-xl font-bold text-white mb-4">Guía Rápida</h2>
        <ul className="space-y-3 text-[#94a3b8]">
          <li>• <strong className="text-white">Hero:</strong> Edita el título, subtítulo y imagen principal</li>
          <li>• <strong className="text-white">Servicios:</strong> Agrega, edita o elimina servicios</li>
          <li>• <strong className="text-white">Precios:</strong> Modifica planes y características</li>
          <li>• <strong className="text-white">Blog:</strong> Crea y administra artículos</li>
          <li>• <strong className="text-white">Mensajes:</strong> Ve los formularios de contacto</li>
        </ul>
      </div>
    </motion.div>
  );
};

export default AdminDashboard;
